/**
 * EmbedCodeTab Component
 * Display and copy embed code for widget integration
 */

import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { EmbedSettingsSchema, type EmbedSettingsFormData } from '../../schemas/widgetConfigSchema';
import { useUpdateEmbedSettings, useEmbedCode } from '../../hooks/useWidgetConfig';
import { FormSection, FormField } from './shared/FormSection';
import { Button } from '../ui/button';
import type { WidgetConfiguration } from '../../types/widget-config';

export interface EmbedCodeTabProps {
  config: WidgetConfiguration;
}

/**
 * EmbedCodeTab shows embed code and allows domain configuration
 */
export function EmbedCodeTab({ config }: EmbedCodeTabProps) {
  const { data: embedCodeData, isLoading: isLoadingEmbedCode } = useEmbedCode();
  const updateEmbedSettings = useUpdateEmbedSettings();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<EmbedSettingsFormData>({
    resolver: zodResolver(EmbedSettingsSchema),
    defaultValues: config.embedSettings,
  });

  const [domainInput, setDomainInput] = React.useState('');
  const allowedDomains = watch('allowedDomains');

  const onSubmit = (data: EmbedSettingsFormData) => {
    updateEmbedSettings.mutate(data);
  };

  const handleCopyEmbedCode = async () => {
    if (embedCodeData?.embedCode) {
      try {
        await navigator.clipboard.writeText(embedCodeData.embedCode);
        toast.success('Embed code copied to clipboard');
      } catch {
        toast.error('Failed to copy embed code');
      }
    }
  };

  const handleCopyUrl = async () => {
    if (embedCodeData?.widgetUrl) {
      try {
        await navigator.clipboard.writeText(embedCodeData.widgetUrl);
        toast.success('Widget URL copied to clipboard');
      } catch {
        toast.error('Failed to copy URL');
      }
    }
  };

  const handleAddDomain = () => {
    const domain = domainInput.trim();
    if (domain && !allowedDomains.includes(domain)) {
      setValue('allowedDomains', [...allowedDomains, domain], { shouldDirty: true });
      setDomainInput('');
    }
  };

  const handleRemoveDomain = (domain: string) => {
    setValue(
      'allowedDomains',
      allowedDomains.filter((d) => d !== domain),
      { shouldDirty: true }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDomain();
    }
  };

  return (
    <div className="space-y-8">
      {/* Widget URL */}
      <FormSection
        title="Widget URL"
        description="Direct link to your booking widget"
      >
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={embedCodeData?.widgetUrl || 'Loading...'}
            readOnly
            className="flex-1 block rounded-md border border-gray-300 px-3 py-2 text-sm bg-gray-50 font-mono"
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleCopyUrl}
            disabled={isLoadingEmbedCode}
          >
            Copy URL
          </Button>
        </div>
      </FormSection>

      {/* Embed Code */}
      <FormSection
        title="Embed Code"
        description="Copy and paste this code into your website HTML"
      >
        <div className="space-y-3">
          <textarea
            value={embedCodeData?.embedCode || 'Loading...'}
            readOnly
            rows={4}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-gray-50 font-mono"
          />
          <Button
            type="button"
            variant="default"
            onClick={handleCopyEmbedCode}
            disabled={isLoadingEmbedCode}
            className="w-full sm:w-auto"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            Copy Embed Code
          </Button>
        </div>

        {/* Installation Instructions */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">
            Installation Instructions
          </h4>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>Copy the embed code above</li>
            <li>Paste it into your website&apos;s HTML where you want the widget to appear</li>
            <li>Save and publish your website</li>
            <li>The widget will automatically load and be ready for bookings</li>
          </ol>
        </div>
      </FormSection>

      {/* Embed Settings Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Allowed Domains */}
        <FormSection
          title="Allowed Domains"
          description="Domains where the widget can be embedded (for CORS security)"
        >
          <FormField
            label="Add Domain"
            description="Enter domain without http:// (e.g., example.com or *.example.com)"
            error={errors.allowedDomains?.message}
          >
            <div className="flex gap-2">
              <input
                type="text"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="example.com"
                className="flex-1 block rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddDomain}
                disabled={!domainInput.trim()}
              >
                Add
              </Button>
            </div>
          </FormField>

          {/* Domain List */}
          {allowedDomains.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-900">
                Configured Domains
              </label>
              <div className="space-y-2">
                {allowedDomains.map((domain) => (
                  <div
                    key={domain}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200"
                  >
                    <span className="text-sm font-mono text-gray-900">
                      {domain}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveDomain(domain)}
                      className="text-red-600 hover:text-red-700"
                      aria-label={`Remove ${domain}`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </FormSection>

        {/* Customization Options */}
        <FormSection
          title="Customization Options"
          description="Configure iframe dimensions"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Default Width"
              error={errors.defaultWidth?.message}
              htmlFor="defaultWidth"
            >
              <input
                {...register('defaultWidth')}
                id="defaultWidth"
                type="text"
                placeholder="100%"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </FormField>

            <FormField
              label="Default Height"
              error={errors.defaultHeight?.message}
              htmlFor="defaultHeight"
            >
              <input
                {...register('defaultHeight')}
                id="defaultHeight"
                type="text"
                placeholder="800px"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </FormField>
          </div>
        </FormSection>

        {/* Analytics */}
        <FormSection
          title="Analytics"
          description="Enable tracking for widget usage"
        >
          <div className="flex items-center gap-2">
            <input
              {...register('analyticsEnabled')}
              id="analyticsEnabled"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label
              htmlFor="analyticsEnabled"
              className="text-sm font-medium text-gray-900"
            >
              Enable analytics tracking
            </label>
          </div>
        </FormSection>

        {/* Save Button */}
        <div className="flex justify-end gap-3 pt-6 border-t">
          <Button
            type="submit"
            disabled={!isDirty || updateEmbedSettings.isPending}
            className="min-w-[120px]"
          >
            {updateEmbedSettings.isPending ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        {/* Unsaved Changes Warning */}
        {isDirty && (
          <p className="text-sm text-amber-600" role="status">
            You have unsaved changes
          </p>
        )}
      </form>
    </div>
  );
}
